import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import styles from './InfoModal.module.css';

const getPitchGuidelines = (environment, ranges) => {
    const guidelines = {
        'Corporate Presentations': {
            description: 'Maintain a balanced, authoritative tone. Aim for consistency with occasional variation for emphasis.',
            tips: ['Use lower pitch for key points', 'Moderate variation for engagement', 'Avoid high pitch that may sound anxious']
        },
        'Social Media/YouTube Content': {
            description: 'More dynamic range is acceptable. Enthusiasm and expressiveness are valued.',
            tips: ['Use varied pitch for engagement', 'Higher pitch for excitement', 'Lower pitch for serious topics']
        },
        'Academic/Technical': {
            description: 'Clear, measured delivery with minimal variation. Focus on clarity and comprehension.',
            tips: ['Consistent pitch for complex information', 'Subtle variations for emphasis', 'Avoid dramatic fluctuations']
        },
        'Motivational Speaking': {
            description: 'Wide pitch range to convey energy and emotion. Strategic variation for impact.',
            tips: ['Higher pitch for inspiration', 'Dynamic range for storytelling', 'Lower pitch for gravitas']
        },
        'TV/Broadcasting': {
            description: 'Professional range with controlled variation. Clear and engaging delivery.',
            tips: ['Balanced pitch for credibility', 'Controlled variation for interest', 'Avoid monotone delivery']
        },
        'Sales/Client Presentations': {
            description: 'Confident and persuasive range. Natural variation to build rapport.',
            tips: ['Moderate pitch for trustworthiness', 'Subtle variations for engagement', 'Avoid aggressive tone']
        }
    };

    const envGuidelines = guidelines[environment] || {
        description: 'Maintain a balanced, professional tone with appropriate variation for your context.',
        tips: ['Adapt pitch to content', 'Use variation strategically', 'Stay within comfortable range']
    };

    const range = ranges?.pitch ? `${ranges.pitch[0]}Hz - ${ranges.pitch[1]}Hz` : '120-180 Hz';

    return { ...envGuidelines, range };
};

const getPauseGuidelines = (environment) => {
    const baseGuidelines = {
        wordPauses: '0.2-0.5s',
        sentencePauses: '0.8-1.5s',
        emphasisPauses: '1.5-2.5s'
    };

    const guidelines = {
        'Corporate Presentations': {
            description: 'Strategic pauses are crucial in corporate settings to convey authority and allow information retention.',
            contextSpecific: 'In corporate presentations, clear segmentation between points and emphasis on key metrics or takeaways is essential.',
            pauseTypes: [
                {
                    type: 'Between Words',
                    duration: baseGuidelines.wordPauses,
                    usage: 'Natural rhythm in speech, allows clear articulation of complex terms or important words'
                },
                {
                    type: 'Between Sentences',
                    duration: baseGuidelines.sentencePauses,
                    usage: 'Clear separation between complete thoughts, allows processing of individual points'
                },
                {
                    type: 'For Emphasis',
                    duration: baseGuidelines.emphasisPauses,
                    usage: 'Highlight key metrics, important announcements, or crucial business points'
                }
            ],
            tips: [
                'Use longer pauses before and after key business metrics',
                'Include strategic pauses after complex information',
                'Insert brief pauses before key terms or important numbers',
                'Allow time for audience note-taking',
                'Use emphasis pauses for crucial takeaways'
            ]
        },
        'Social Media/YouTube Content': {
            description: 'Dynamic and engaging pausing patterns keep viewers interested while maintaining authenticity.',
            contextSpecific: 'Online content requires more varied pacing to maintain viewer engagement and emphasize key points.',
            pauseTypes: [
                {
                    type: 'Between Words',
                    duration: '0.1-0.3s',
                    usage: 'Quick, energetic delivery while maintaining clarity'
                },
                {
                    type: 'Between Sentences',
                    duration: '0.6-1.2s',
                    usage: 'Clear breaks between points while maintaining viewer engagement'
                },
                {
                    type: 'For Emphasis',
                    duration: '1.0-2.0s',
                    usage: 'Dramatic pauses for reveals, surprises, or important points'
                }
            ],
            tips: [
                'Use dramatic pauses before reveals or key points',
                'Keep a faster overall pace with shorter pauses',
                'Insert longer pauses for audience reaction time',
                'Vary pause lengths to maintain dynamic delivery',
                'Use natural breaks to seem more authentic'
            ]
        },
        'Academic/Technical': {
            description: 'Deliberate pausing patterns optimize information retention and understanding of complex concepts.',
            contextSpecific: 'Technical content requires careful pacing to ensure comprehension of complex information.',
            pauseTypes: [
                {
                    type: 'Between Words',
                    duration: '0.2-0.5s',
                    usage: 'Clear articulation of technical terms and complex vocabulary'
                },
                {
                    type: 'Between Sentences',
                    duration: '1.0-2.0s',
                    usage: 'Allow processing of complete technical concepts'
                },
                {
                    type: 'For Emphasis',
                    duration: '1.5-2.5s',
                    usage: 'Highlight key concepts, definitions, or critical information'
                }
            ],
            tips: [
                'Use longer pauses after technical terms',
                'Insert regular pauses for note-taking',
                'Include extended pauses after complex explanations',
                'Allow time for comprehension of difficult concepts',
                'Pause before and after key definitions'
            ]
        },
        'Motivational Speaking': {
            description: 'Strategic use of silence creates impact and emotional connection.',
            contextSpecific: 'Effective pausing helps build anticipation and emphasize powerful messages.',
            pauseTypes: [
                {
                    type: 'Between Words',
                    duration: '0.2-0.4s',
                    usage: 'Dynamic and engaging delivery'
                },
                {
                    type: 'Between Sentences',
                    duration: '0.8-1.5s',
                    usage: 'Allow messages to sink in'
                },
                {
                    type: 'For Emphasis',
                    duration: '1.5-3.0s',
                    usage: 'Create dramatic impact and emotional resonance'
                }
            ],
            tips: [
                'Use dramatic pauses for emotional impact',
                'Allow silence after powerful statements',
                'Build anticipation with strategic pauses',
                'Give time for audience reflection',
                'Use pauses to emphasize key messages'
            ]
        },
        'TV/Broadcasting': {
            description: 'Professional pacing with precise pauses maintains audience engagement.',
            contextSpecific: 'Broadcast environments require well-timed pauses for clarity and impact.',
            pauseTypes: [
                {
                    type: 'Between Words',
                    duration: '0.1-0.3s',
                    usage: 'Clear, professional delivery'
                },
                {
                    type: 'Between Sentences',
                    duration: '0.7-1.2s',
                    usage: 'Clean breaks between statements'
                },
                {
                    type: 'For Emphasis',
                    duration: '1.0-2.0s',
                    usage: 'Highlight important news or information'
                }
            ],
            tips: [
                'Maintain consistent pause patterns',
                'Use brief pauses for clear articulation',
                'Include strategic pauses for emphasis',
                'Allow natural breaks for comprehension',
                'Time pauses with visual elements'
            ]
        },
        'Sales/Client Presentations': {
            description: 'Well-timed pauses allow for client engagement and message absorption.',
            contextSpecific: 'Effective pausing creates opportunities for dialogue and builds trust.',
            pauseTypes: [
                {
                    type: 'Between Words',
                    duration: '0.2-0.4s',
                    usage: 'Natural, conversational delivery'
                },
                {
                    type: 'Between Sentences',
                    duration: '0.8-1.5s',
                    usage: 'Allow client processing and potential questions'
                },
                {
                    type: 'For Emphasis',
                    duration: '1.5-2.5s',
                    usage: 'Highlight value propositions and key benefits'
                }
            ],
            tips: [
                'Pause to invite client questions',
                'Use emphasis pauses for key benefits',
                'Allow time for client consideration',
                'Create natural dialogue opportunities',
                'Pause after important proposals'
            ]
        }
    };

    return guidelines[environment] || {
        description: 'Balance your speaking rhythm with appropriate pauses for your context.',
        contextSpecific: 'Adapt pause lengths to your specific speaking situation and audience needs.',
        pauseTypes: [
            {
                type: 'Between Words',
                duration: baseGuidelines.wordPauses,
                usage: 'Natural speech rhythm and clear articulation'
            },
            {
                type: 'Between Sentences',
                duration: baseGuidelines.sentencePauses,
                usage: 'Clear separation between complete thoughts'
            },
            {
                type: 'For Emphasis',
                duration: baseGuidelines.emphasisPauses,
                usage: 'Highlight important points or key messages'
            }
        ],
        tips: [
            'Adapt pause length to content complexity',
            'Use longer pauses for emphasis',
            'Maintain consistent rhythm for clarity',
            'Include breaks for audience processing',
            'Vary pauses based on message importance'
        ]
    };
};

const getClarityGuidelines = () => {
    return {
        description: 'Speech clarity is essential for effective communication, ensuring that your words are easily understood. It involves precise pronunciation, proper articulation, and the overall intelligibility of speech, helping your message resonate clearly with your audience.',
        components: [
            {
                tips: [
                    'Practice challenging words beforehand',
                    'Use clear vowel sounds',
                    'Maintain distinction between similar sounds',
                    'Be mindful of regional variations'
                ]
            }
        ],
        commonIssues: [
            'Mumbling or unclear word endings',
            'Speaking too quickly',
            'Inconsistent volume levels',
            'Poor breath support affecting clarity',
            'Slurring words together'
        ],
        improvementStrategies: [
            'Record and analyze your speech',
            'Practice tongue twisters',
            'Focus on breath support',
            'Slow down for complex terms',
            'Regular vocal warm-ups'
        ]
    };
};

const InfoModal = ({ type, environment, ranges }) => {
    const [isOpen, setIsOpen] = useState(false);

    const getContent = () => {
        switch (type) {
            case 'pitch':
                const pitchGuidelines = getPitchGuidelines(environment, ranges);
                return {
                    title: 'Voice Pitch Analysis',
                    content: (
                        <div className={styles.contentGrid}>
                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>What is Voice Pitch?</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <p>Voice pitch is the perceived frequency of your voice, measured in Hertz (Hz). It's how high or low your voice sounds and is a crucial element of vocal delivery.</p>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Why Pitch Matters</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <p>Pitch variation can significantly impact how your message is received. It helps convey emotion, emphasis, and engagement with your audience. Monotone delivery can reduce listener attention, while appropriate variation maintains interest.</p>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Recommended Range</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <div className={styles.rangeCard}>
                                        <span className={styles.rangeLabel}>Target Range:</span>
                                        <span className={styles.rangeValue}>{pitchGuidelines.range}</span>
                                    </div>
                                    <p>{pitchGuidelines.description}</p>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Best Practices</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <ul className={styles.bestPractices}>
                                        {pitchGuidelines.tips.map((tip, index) => (
                                            <li key={index}>{tip}</li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        </div>
                    )
                };

            case 'pause':
                const pauseGuidelines = getPauseGuidelines(environment);
                return {
                    title: 'Speaking Rhythm Analysis',
                    content: (
                        <div className={styles.contentGrid}>
                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Understanding Pauses</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <p>Pauses are strategic breaks in speech that serve multiple purposes: emphasizing points, allowing audience comprehension, creating natural rhythm, and building anticipation.</p>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Strategic Impact</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <p>{pauseGuidelines.contextSpecific}</p>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Pause Types</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <div className={styles.pauseGrid}>
                                        {pauseGuidelines.pauseTypes.map((pause, index) => (
                                            <div key={index} className={styles.pauseCard}>
                                                <h4>{pause.type}</h4>
                                                <div className={styles.pauseDetails}>
                                                    <span className={styles.pauseDuration}>{pause.duration}</span>
                                                    <p>{pause.usage}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Best Practices</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <ul className={styles.bestPractices}>
                                        {pauseGuidelines.tips.map((tip, index) => (
                                            <li key={index}>{tip}</li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        </div>
                    )
                };

            case 'clarity':
                const clarityGuidelines = getClarityGuidelines();
                return {
                    title: 'Speech Clarity Analysis',
                    content: (
                        <div className={styles.contentGrid}>
                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Understanding Speech Clarity</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <p>{clarityGuidelines.description}</p>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Pronunciation</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <div className={styles.componentsGrid}>
                                        {clarityGuidelines.components.map((component, index) => (
                                            <div key={index} className={styles.componentCard}>
                                                <h4>{component.title}</h4>
                                                <p>{component.description}</p>
                                                <ul className={styles.bestPractices}>
                                                    {component.tips.map((tip, tipIndex) => (
                                                        <li key={tipIndex}>{tip}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Common Issues</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <ul className={styles.bestPractices}>
                                        {clarityGuidelines.commonIssues.map((issue, index) => (
                                            <li key={index}>{issue}</li>
                                        ))}
                                    </ul>
                                </div>
                            </section>

                            <section className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h3>Improvement Strategies</h3>
                                </div>
                                <div className={styles.sectionContent}>
                                    <ul className={styles.bestPractices}>
                                        {clarityGuidelines.improvementStrategies.map((strategy, index) => (
                                            <li key={index}>{strategy}</li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        </div>
                    )
                };

            default:
                return {
                    title: 'Information',
                    content: <p>No information available.</p>
                };
        }
    };
    // eslint-disable-next-line
    const { title, content } = getContent();

    return (
        <div className={styles.container}>
            <button
                className={styles.expandButton}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
            >
                <div className={styles.buttonContent}>
                    <span>
                        {type === 'pitch'
                            ? 'Voice Pitch Analysis'
                            : type === 'clarity'
                                ? 'Speech Clarity Analysis'
                                : 'Speaking Rhythm Analysis'}
                    </span>
                    {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
            </button>

            {isOpen && (
                <div className={styles.modalContent}>
                    <div className={styles.modalHeader}>
                        <p className={styles.environmentTag}>{environment}</p>
                    </div>
                    {content}
                </div>
            )}
        </div>
    );
};

export default InfoModal;